import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** SOC module (Huntress) — mirrors server/soc/. Read-only. */

export type OrgLinkSource = 'manual' | 'ignored' | 'tenant' | 'name' | 'none';
export interface SocOrg {
  id: number; name: string; key: string; tenantId: string | null;
  agents: number; incidents: number; billableIdentities: number; logSources: number;
  company: { companyId: number | null; companyName: string | null; source: OrgLinkSource; by: string | null };
  portalUrl: string;
}

export type AgentHealth = 'healthy' | 'defender' | 'silent' | 'stale' | 'isolated' | 'no-edr';
export interface SocAgent {
  id: number; orgId: number; orgName: string; hostname: string; platform: string | null; os: string | null;
  serial: string | null; externalIp: string | null; ip: string | null; version: string | null; edrInstalled: boolean;
  lastCallback: string | null; defender: string | null; defenderDetail: string | null; defenderHealthy: boolean | null;
  isolation: string | null; isolated: boolean; tamperOff: boolean; health: AgentHealth;
  companyId: number | null; companyName: string | null;
  rmm: { uid: string; hostname: string; online: boolean; matchedBy: 'tag' | 'serial' | 'mac' | 'hostname' } | null;
  portalUrl: string;
}

export interface SocIncident {
  id: number; orgId: number; orgName: string; agentId: number | null; hostname: string | null; platform: string | null;
  severity: string | null; status: string | null; subject: string; summary: string | null;
  sentAt: string | null; closedAt: string | null; statusUpdatedAt: string | null; indicatorTypes: string[];
  ticketIds: number[]; remediations: number; companyId: number | null; companyName: string | null; portalUrl: string;
}

export interface SocIdentity {
  id: number; orgId: number | null; orgName: string | null; upn: string | null; enabled: boolean | null; billable: boolean | null;
  mfa: boolean | null; riskLevel: string | null; riskState: string | null; passwordChanged: string | null; synced: boolean | null; external: boolean | null;
  companyId: number | null; companyName: string | null;
}

export interface SocEscalation {
  id: number; orgIds: number[]; orgNames: string[]; subject: string; type: string | null; subtype: string | null; severity: string | null;
  status: string | null; createdAt: string | null; resolvedAt: string | null; ticketIds: number[]; companyId: number | null; companyName: string | null;
}

export interface SocResponse {
  configured: boolean;
  orgs: SocOrg[];
  agents: SocAgent[];
  incidents: SocIncident[];
  escalations: SocEscalation[] | null;
  identitiesLoaded: boolean;
  settings: { silentHours: number; staleDays: number };
  fetchedAt: string;
}

/** Orgs, agents, incidents, escalations. `scoped`: narrow to the header client picker. */
export function useSoc(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<SocResponse>(opts.scoped, (d, inScope) => d.configured ? ({
    ...d,
    orgs: d.orgs.filter((x) => inScope(x.company.companyId)),
    agents: d.agents.filter((x) => inScope(x.companyId)),
    incidents: d.incidents.filter((x) => inScope(x.companyId)),
    escalations: d.escalations?.filter((x) => inScope(x.companyId)) ?? null,
  }) : d);
  return useQuery({ select, queryKey: ['soc'], queryFn: () => api.get<SocResponse>('/api/soc'), staleTime: 60_000, refetchInterval: 5 * 60_000 });
}

export function useSocIdentities(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ configured: boolean; items: SocIdentity[] | null; fetchedAt: string | null }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items?.filter((x) => inScope(x.companyId)) ?? null }));
  return useQuery({
    select,
    queryKey: ['soc', 'identities'],
    queryFn: () => api.get<{ configured: boolean; items: SocIdentity[] | null; fetchedAt: string | null }>('/api/soc/identities'),
    staleTime: 5 * 60_000,
    // The identity cache loads in the background after the rest; poll until it arrives.
    refetchInterval: (q) => (q.state.data && q.state.data.items == null && q.state.data.configured ? 10_000 : false),
  });
}

export interface SocIncidentDetail {
  incident: SocIncident;
  agent: SocAgent | null;
  body: string | null;
  remediations: { id: number; type: string | null; action: string | null; status: string | null; approvedAt: string | null; completedAt: string | null }[];
  error?: string;
}

export function useSocIncident(id: number | undefined) {
  return useQuery({
    enabled: id != null && Number.isFinite(id),
    queryKey: ['soc', 'incident', id],
    queryFn: () => api.get<SocIncidentDetail>(`/api/soc/incidents/${id}`),
    staleTime: 60_000,
  });
}

/** Huntress incidents/escalations that raised an Autotask ticket (the ticket's SOC card). */
export function useSocTicket(ticketId: number | null | undefined, enabled: boolean) {
  return useQuery({
    enabled: enabled && !!ticketId,
    queryKey: ['soc', 'ticket', ticketId],
    queryFn: () => api.get<{ incidents: (SocIncident & { agent: SocAgent | null })[]; escalations: SocEscalation[] }>(`/api/soc/tickets/${ticketId}`),
    staleTime: 60_000,
  });
}

/** Huntress agents matched to a Datto RMM device uid. */
export function useSocDevice(uid: string | null | undefined) {
  return useQuery({
    enabled: !!uid,
    queryKey: ['soc', 'device', uid],
    queryFn: () => api.get<{ configured: boolean; agents: SocAgent[]; incidents: SocIncident[] }>(`/api/soc/devices/${uid}`),
    staleTime: 60_000,
  });
}
